/**
 * Zendesk backend proxy gateway.
 *
 * Routes Zendesk API calls through the Django backend.
 * No ZD-Token — the backend authenticates via JWT and resolves the member
 * server-side.
 */
import axios, { AxiosRequestHeaders } from 'axios';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import config from 'modules/common/config';
import { localViews } from 'modules/Auth/AuthProvider/views';
import { refreshAccessToken, isAccessTokenExpiringSoon } from 'modules/Auth/AuthProvider/tokenRefresh';
import { forceLogoutRedirect } from 'modules/Auth/AuthProvider/forceLogout';
import { auth0AuthorizationHeader, renewRejectedAuth0Bearer } from 'modules/Auth/auth0/tokenBridge';
import { getStaffHeaders } from 'modules/common/getCollectiveHeaders';

// No Authorization at all without a Django token: XHR merges an empty value with the Auth0
// bearer Uppy adds, and the backend cannot read the result.
export const getHeaders = (): Record<string, string> => {
    const localToken = localViews.token();
    const token = localToken?.access;
    return token ? { Authorization: `JWT ${token}` } : {};
};

const zendeskBackendGateway = axios.create({
    baseURL: `${config.djangoURL}zendesk/proxy`,
    withCredentials: true,
});

zendeskBackendGateway.interceptors.request.use(async (cfg) => {
    const headers = { ...(cfg.headers || {}) } as AxiosRequestHeaders;

    // The same Django-first credential collectiveApi sends, decided in tokenBridge. Auth0's
    // SDK owns its expiry and refresh, so the Django refresh is skipped for it.
    const auth0Authorization = await auth0AuthorizationHeader();
    if (auth0Authorization) {
        headers.Authorization = auth0Authorization;
    } else {
        if (isAccessTokenExpiringSoon()) {
            await refreshAccessToken();
        }

        const localToken = localViews.token();
        const token = localToken?.access;
        headers.Authorization = token ? `JWT ${token}` : '';
    }

    // While impersonating, as collectiveApi sends it; rejects rather than send without it.
    Object.assign(headers, await getStaffHeaders());

    // For uploads, let browser handle Content-Type automatically
    if ((cfg.url || '').includes('/uploads.json')) {
        delete (headers as any)['Content-Type'];
    } else {
        (headers as any)['Content-Type'] = 'application/json';
    }

    return { ...cfg, headers, auth0Bearer: Boolean(auth0Authorization) };
});

zendeskBackendGateway.interceptors.response.use(
    (response) => response,
    async (error) => {
        sendDatadogRumError('Zendesk backend proxy error', error, {
            url: error?.config?.url,
            method: error?.config?.method,
        });

        if (error.response?.status === 401) {
            // One retry with a renewed Auth0 bearer, as collectiveApi does. A 401 on the retry signs
            // out in its own pass; any other failure on it only rejects.
            if (
                error.config?.auth0Bearer &&
                !error.config.retriedAfterRefresh &&
                (await renewRejectedAuth0Bearer(error.response.data))
            ) {
                return zendeskBackendGateway({ ...error.config, retriedAfterRefresh: true });
            }
            forceLogoutRedirect();
        }

        return Promise.reject(error);
    }
);

export default zendeskBackendGateway;
